import { Button, Collapse, Tag as AntTag, Tooltip } from 'antd';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import type { TagProtectionDetail } from '../../../interfaces/AccessMap';
import { getSettingIcon, getSettingPath, getSettingTitle } from '../../../utils/settingSections';
import { Icon } from '../../Core/Icon';
import RecipientsList from './RecipientsList';

interface Props {
  protection: TagProtectionDetail;
  hideRecipients?: boolean;
  defaultExpanded?: boolean;
}

const COLLAPSE_OVERRIDES = clsx(
  'border-gray-medium/60! dark:bg-gray-light! overflow-hidden rounded-md! border! bg-white!',
  '[&_.ant-collapse-item]:border-b-0!',
  'dark:[&_.ant-collapse-header]:bg-gray-light! [&_.ant-collapse-header]:bg-white!',
  '[&_.ant-collapse-header]:px-3! [&_.ant-collapse-header]:py-2.5!',
  '[&_.ant-collapse-header]:items-center!',
  '[&_.ant-collapse-header:hover]:bg-azure-light/40! dark:[&_.ant-collapse-header:hover]:bg-gray!',
  'dark:[&_.ant-collapse-content]:bg-gray-light! [&_.ant-collapse-content]:bg-white!',
  '[&_.ant-collapse-content]:border-gray-medium/40! [&_.ant-collapse-content]:border-t!',
  '[&_.ant-collapse-content-box]:px-3! [&_.ant-collapse-content-box]:pt-3! [&_.ant-collapse-content-box]:pb-3!',
);

export const ProtectionCard = ({
  protection,
  hideRecipients = false,
  defaultExpanded = false,
}: Props) => {
  const { t } = useTranslation();
  const iconName = getSettingIcon(protection.type);
  const title = getSettingTitle(protection.type, t);
  const path = getSettingPath(protection.type);
  const goToSetting = () => {
    window.location.assign(path);
  };

  const exceptionsCount = protection.exceptions.users.length + protection.exceptions.groups.length;

  const header = (
    <div className="flex w-full flex-row items-center justify-between gap-2">
      <div className="flex min-w-0 flex-row items-center gap-2">
        {iconName && <Icon name={iconName} className="text-blue-dark h-5 w-5 shrink-0" />}
        <span className="text-blue-dark truncate font-semibold">{title}</span>
      </div>
      <Tooltip title={t('accessMap.openInSettings')}>
        <Button
          size="small"
          type="text"
          aria-label={t('accessMap.openInSettings')}
          icon={<Icon name="ExternalLinkIcon" className="text-blue-dark h-4 w-4" />}
          onClick={(e) => {
            e.stopPropagation();
            goToSetting();
          }}
        />
      </Tooltip>
    </div>
  );

  return (
    <Collapse
      size="small"
      defaultActiveKey={defaultExpanded ? ['1'] : []}
      className={COLLAPSE_OVERRIDES}
      items={[
        {
          children: (
            <div className="flex flex-col gap-4">
              {protection.summary.length > 0 && (
                <div>
                  <p className="text-blue-dark mb-1.5 text-xs font-semibold tracking-wide uppercase">
                    {t('accessMap.configuration')}
                  </p>
                  <dl className="bg-azure-light text-blue-dark dark:bg-gray-light divide-blue-dark/10 divide-y overflow-hidden rounded-md text-sm">
                    {protection.summary.map((entry, idx) => (
                      <div
                        key={idx}
                        className="flex flex-row items-baseline justify-between gap-4 px-3 py-2"
                      >
                        <dt className="text-blue-dark/75 shrink-0">{t(entry.label)}</dt>
                        <dd className="text-right font-semibold break-words">{entry.value}</dd>
                      </div>
                    ))}
                  </dl>
                </div>
              )}
              {!hideRecipients && (
                <div>
                  <p className="text-blue-dark mb-1.5 text-xs font-semibold tracking-wide uppercase">
                    {t('accessMap.appliedTo')}
                  </p>
                  {protection.appliedToAll ? (
                    <AntTag color="green" className="text-sm">
                      <Icon name="EyeIcon" className="mr-1 inline-block h-3.5 w-3.5" />
                      {t('accessMap.allUsersInCompany')}
                    </AntTag>
                  ) : (
                    <RecipientsList
                      users={protection.appliedTo.users}
                      groups={protection.appliedTo.groups}
                      compact
                      emptyText={t('accessMap.appliedToNobody')}
                    />
                  )}
                </div>
              )}
              {exceptionsCount > 0 && (
                <div>
                  <p className="text-blue-dark mb-1.5 text-xs font-semibold tracking-wide uppercase">
                    {t('accessMap.exceptions')}
                  </p>
                  <RecipientsList
                    users={protection.exceptions.users}
                    groups={protection.exceptions.groups}
                    compact
                  />
                </div>
              )}
              <Button
                type="link"
                className="text-blue-dark! hover:text-azure! self-start! pl-0!"
                icon={<Icon name="ExternalLinkIcon" className="h-4 w-4" />}
                onClick={goToSetting}
              >
                {t('accessMap.openInSettings')}
              </Button>
            </div>
          ),
          key: '1',
          label: header,
        },
      ]}
    />
  );
};

export default ProtectionCard;
